import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminFetch } from '../hooks/useAdminFetch'

interface AuditItem {
  id: string
  event_type: string
  tenant_id: string | null
  tenant_name: string | null
  admin_email: string | null
  payload: Record<string, unknown>
  ip_address: string | null
  created_at: string | null
}

interface AuditResponse {
  items: AuditItem[]
  total: number
  limit: number
  offset: number
}

const EVENT_LABELS: Record<string, string> = {
  admin_login: 'Login admin',
  admin_login_failed: 'Login fallito',
  admin_overview_viewed: 'Overview visualizzata',
  admin_list_tenants: 'Lista tenant',
  admin_tenant_detail_viewed: 'Scheda tenant',
  admin_impersonation_started: 'Impersonazione avviata',
  admin_job_reprocess: 'Job riprocessato',
  admin_flush_kpi_cache: 'Cache KPI svuotata',
  plan_change: 'Cambio piano',
  trial_extended: 'Trial esteso',
  rate_limit_override_set: 'Override rate limit',
}

const EVENT_COLORS: Record<string, string> = {
  admin_login_failed: 'bg-red-500/15 text-red-300',
  admin_impersonation_started: 'bg-amber-500/15 text-amber-300',
  plan_change: 'bg-purple-500/15 text-purple-300',
  trial_extended: 'bg-blue-500/15 text-blue-300',
  admin_job_reprocess: 'bg-cyan-500/15 text-cyan-300',
  rate_limit_override_set: 'bg-orange-500/15 text-orange-300',
}

const LIMIT = 50

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function EventBadge({ type }: { type: string }) {
  const cls = EVENT_COLORS[type] ?? 'bg-gray-500/15 text-gray-400'
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls}`}>
      {EVENT_LABELS[type] ?? type}
    </span>
  )
}

export function AdminAuditLog() {
  const [eventType, setEventType] = useState('')
  const [tenantFilter, setTenantFilter] = useState('')
  const [adminFilter, setAdminFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) })
  if (eventType) params.set('event_type', eventType)
  if (tenantFilter) params.set('tenant_id', tenantFilter)
  if (adminFilter) params.set('admin_email', adminFilter)
  if (dateFrom) params.set('date_from', dateFrom)
  if (dateTo) params.set('date_to', dateTo)
  if (search) params.set('search', search)

  const { data, isLoading } = useQuery<AuditResponse>({
    queryKey: ['admin', 'audit-log', eventType, tenantFilter, adminFilter, dateFrom, dateTo, search, offset],
    queryFn: () => adminFetch(`/api/v1/admin/audit-log?${params}`),
  })

  function handleExport() {
    const exportParams = new URLSearchParams()
    if (eventType) exportParams.set('event_type', eventType)
    if (tenantFilter) exportParams.set('tenant_id', tenantFilter)
    if (adminFilter) exportParams.set('admin_email', adminFilter)
    if (dateFrom) exportParams.set('date_from', dateFrom)
    if (dateTo) exportParams.set('date_to', dateTo)
    if (search) exportParams.set('search', search)

    const token = localStorage.getItem('km_admin_token') ?? ''
    const url = `/api/v1/admin/audit-log/export?${exportParams}`
    fetch(url, { headers: { 'X-Admin-Token': token } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
      })
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--km-bg)',
    borderColor: 'var(--km-border)',
    color: 'var(--km-text-secondary)',
  }

  const ALL_EVENTS = [
    'admin_login', 'admin_login_failed', 'admin_impersonation_started',
    'admin_job_reprocess', 'admin_flush_kpi_cache',
    'plan_change', 'trial_extended', 'rate_limit_override_set',
  ]

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--km-text-primary)' }}>
            Audit Log
          </h2>
          <p className="text-sm mt-0.5" style={{ color: 'var(--km-text-muted)' }}>
            Tutte le azioni rilevanti sulla piattaforma
          </p>
        </div>
        <button
          onClick={handleExport}
          className="px-3 py-2 rounded-lg text-xs border transition-colors"
          style={{
            background: 'var(--km-surface)',
            borderColor: 'var(--km-border)',
            color: 'var(--km-text-secondary)',
          }}
        >
          ↓ Esporta CSV
        </button>
      </div>

      {/* Filtri */}
      <div
        className="rounded-xl border p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <select
          value={eventType}
          onChange={(e) => { setEventType(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-xs border outline-none"
          style={inputStyle}
        >
          <option value="">Tutti gli eventi</option>
          {ALL_EVENTS.map((e) => (
            <option key={e} value={e}>{EVENT_LABELS[e] ?? e}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Tenant ID o nome"
          value={tenantFilter}
          onChange={(e) => { setTenantFilter(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-xs border outline-none"
          style={inputStyle}
        />
        <input
          type="text"
          placeholder="Admin email"
          value={adminFilter}
          onChange={(e) => { setAdminFilter(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-xs border outline-none"
          style={inputStyle}
        />
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-xs border outline-none"
          style={inputStyle}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-xs border outline-none"
          style={inputStyle}
        />
        <input
          type="text"
          placeholder="Cerca nel payload…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-xs border outline-none"
          style={inputStyle}
        />
      </div>

      {/* Tabella */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--km-border)' }}>
        {isLoading && (
          <div className="p-8 text-sm text-center" style={{ color: 'var(--km-text-muted)' }}>
            Caricamento…
          </div>
        )}
        {data && (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-xs uppercase tracking-wider border-b"
                  style={{
                    background: 'var(--km-surface)',
                    borderColor: 'var(--km-border)',
                    color: 'var(--km-text-muted)',
                  }}
                >
                  <th className="text-left px-4 py-3 font-medium">Timestamp</th>
                  <th className="text-left px-4 py-3 font-medium">Evento</th>
                  <th className="text-left px-4 py-3 font-medium">Tenant</th>
                  <th className="text-left px-4 py-3 font-medium">Admin</th>
                  <th className="text-left px-4 py-3 font-medium">IP</th>
                  <th className="text-left px-4 py-3 font-medium">Payload</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--km-text-muted)' }}>
                      Nessun evento trovato
                    </td>
                  </tr>
                ) : (
                  data.items.map((item) => (
                    <>
                      <tr
                        key={item.id}
                        className="border-b hover:bg-white/[0.02] transition-colors cursor-pointer"
                        style={{ borderColor: 'var(--km-border)' }}
                        onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                      >
                        <td className="px-4 py-3 font-mono text-xs whitespace-nowrap" style={{ color: 'var(--km-text-muted)' }}>
                          {formatDate(item.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <EventBadge type={item.event_type} />
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--km-text-secondary)' }}>
                          {item.tenant_name ?? (item.tenant_id ? item.tenant_id.slice(0, 8) + '…' : '—')}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--km-text-secondary)' }}>
                          {item.admin_email ?? '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--km-text-muted)' }}>
                          {item.ip_address ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--km-text-muted)' }}>
                          {expandedId === item.id ? '▲ chiudi' : '▼ mostra'}
                        </td>
                      </tr>
                      {expandedId === item.id && (
                        <tr key={`${item.id}-payload`} style={{ borderColor: 'var(--km-border)' }}>
                          <td colSpan={6} className="px-4 py-3">
                            <pre
                              className="text-xs rounded-lg p-3 overflow-x-auto"
                              style={{
                                background: 'var(--km-bg)',
                                color: 'var(--km-text-secondary)',
                                borderColor: 'var(--km-border)',
                              }}
                            >
                              {JSON.stringify(item.payload, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>

            {/* Paginazione */}
            {data.total > LIMIT && (
              <div
                className="flex items-center justify-between px-4 py-3 border-t"
                style={{ borderColor: 'var(--km-border)', background: 'var(--km-surface)' }}
              >
                <span className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
                  {offset + 1}–{Math.min(offset + LIMIT, data.total)} di {data.total} eventi
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                    className="px-3 py-1.5 rounded-lg text-xs border disabled:opacity-40"
                    style={{ background: 'var(--km-bg)', borderColor: 'var(--km-border)', color: 'var(--km-text-secondary)' }}
                  >
                    Precedente
                  </button>
                  <button
                    disabled={offset + LIMIT >= data.total}
                    onClick={() => setOffset(offset + LIMIT)}
                    className="px-3 py-1.5 rounded-lg text-xs border disabled:opacity-40"
                    style={{ background: 'var(--km-bg)', borderColor: 'var(--km-border)', color: 'var(--km-text-secondary)' }}
                  >
                    Successiva
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
